import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import PaginationBar from './components/PaginationBar';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import BensModal from './components/modals/BensModal';
import usePagination10 from './components/hooks/usePagination10';
import useReferenceData from './components/hooks/useReferenceData';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import { formatCurrency, fmtCurrency } from './utils/formatMoeda';
import { toIsoDate } from './utils/dateUtils';
import Toolbar from './components/Toolbar';
import LoadingModal from './components/modals/LoadingModal';

const emptyForm = {
  id: '', ano: '', idPrograma: '', idAcao: '', idEscola: '', idConta: '', idFavorecido: '',
  idNatureza: '', idTipoBem: '', docNum: '', docData: '',
  docTipo: '', descricao: '', qtde: '', vlr: '', desconto: ''
};

function Bens() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const ano = localStorage.getItem('ano');
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ ...emptyForm, idEscola: cnpj || '', ano: ano || '' });
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(items.length);
  const { programas, acoes, contas, favorecidos, naturezas, tiposBens } = useReferenceData(token, cnpj);
  // Agora o backend já retorna apenas os bens da despesa, se for o caso
  const displayed = items.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  useEffect(() => {
    async function fetchBens() {
      setIsLoading(true);
      try {
        let response;
        if (location.state?.fromDespesa) {
          const d = location.state.fromDespesa;
          // Garante docData no formato ISO ou não envia se vazio
          let docDataIso = '';
          if (d.docData instanceof Date) {
            docDataIso = d.docData.toISOString().slice(0, 10);
          } else if (typeof d.docData === 'string') {
            // Se vier como "2025,3,12" ou similar, tenta converter para ISO
            if (/^\d{4},\d{1,2},\d{1,2}$/.test(d.docData)) {
              const [y, m, d2] = d.docData.split(',');
              // m-1 porque JS Date usa mês 0-based
              const dt = new Date(Number(y), Number(m) - 1, Number(d2));
              docDataIso = dt.toISOString().slice(0, 10);
            } else if (/^\d{4}-\d{2}-\d{2}/.test(d.docData)) {
              docDataIso = d.docData.slice(0, 10);
            } else {
              docDataIso = '';
            }
          } else {
            docDataIso = '';
          }
          const params = {
            ano: d.ano,
            idPrograma: d.idPrograma,
            idAcao: d.idAcao,
            idEscola: d.idEscola,
            idConta: d.idConta,
            idFavorecido: d.idFavorecido,
            idNatureza: d.idNatureza,
            docNum: d.docNum
          };
          if (docDataIso) params.docData = docDataIso;
          response = await axios.get(`${API_BASE_URL.BENS}/byDespesa`, {
            params,
            headers: { Authorization: `Bearer ${token}` }
          });
        } else if (cnpj && ano) {
          response = await axios.get(`${API_BASE_URL.BENS}/all`, {
            params: { idEscola: cnpj, ano }, headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          showDefaultModal('error', 'Escola ou ano não selecionado.');
          setIsLoading(false);
          return;
        }
        setItems(response.data);
      } catch (err) {
        if (err.response && err.response.status !== 200 && err.response.status !== 404) {
          showDefaultModal('error', 'Erro ao carregar bens.');
        }
      } finally {
        setIsLoading(false);
      }
    }
    fetchBens();
  }, [token, cnpj, ano, atualizar, location.state]);

  // Ao chegar da página Despesas via navigate: pré-preenche o formulário sem abrir o modal
  useEffect(() => {
    if (location.state?.fromDespesa) {
      const d = location.state.fromDespesa;
    function normDocData(docData) {
      if (!docData) return '';
      if (Array.isArray(docData)) {
        const [y, m, dd] = docData;
        return `${y}-${String(m).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
      }
      if (typeof docData === 'string') {
        if (/^\d{4},\d{1,2},\d{1,2}$/.test(docData)) {
          const [y, m, dd] = docData.split(',');
          return `${y}-${String(m).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
        }
        return docData.slice(0, 10);
      }
      return String(docData);
    }
    setForm({ ...emptyForm, id: '', ano: d.ano, idPrograma: d.idPrograma, idAcao: d.idAcao,
        idEscola: d.idEscola, idConta: d.idConta, idFavorecido: d.idFavorecido,
        idNatureza: d.idNatureza, idTipoBem: d.idTipoBem, docNum: d.docNum,
        docData: normDocData(d.docData), docTipo: d.docTipo || '', descricao: '', qtde: '', vlr: '' });
    }
  }, [location.state]);

  function handleChange(e) { const { name, value } = e.target; setForm(f => ({ ...f, [name]: value })); }
  function handleNew() {
    if (!location.state?.fromDespesa) {
      showDefaultModal('error', 'Só é possível cadastrar bens a partir de uma despesa capital.');
      return;
    }
    const d = location.state.fromDespesa;
    setForm({
      ...emptyForm,
      id: '',
      ano: d.ano,
      idPrograma: d.idPrograma,
      idAcao: d.idAcao,
      idEscola: d.idEscola,
      idConta: d.idConta,
      idFavorecido: d.idFavorecido,
      idNatureza: d.idNatureza,
      idTipoBem: d.idTipoBem,
      docNum: d.docNum,
      docData: d.docData,
      docTipo: d.docTipo || '',
      descricao: '',
      qtde: '',
      vlr: ''
    });
    setShowModal(true);
  }
  function handleEdit() { if (selected.length !== 1) return; const s = selected[0]; setForm({ ...s, docData: toIsoDate(s.docData) }); setShowModal(true); }

  function handleSave(e) {
    e.preventDefault();
    if (!form.docData) {
      showDefaultModal('error', 'A data do documento não foi informada. Volte para a despesa e tente novamente.');
      return;
    }
    // Se for capital, idTipoBem é obrigatório
    if (form.idNatureza && String(form.idNatureza).toUpperCase().includes('K') && !form.idTipoBem) {
      showDefaultModal('error', 'O campo Tipo de Bem é obrigatório para bens de natureza capital.');
      return;
    }
    // Se vier de uma despesa, limitar o valor total dos bens
    if (location.state?.fromDespesa) {
      const despesa = location.state.fromDespesa;
      const bensMesmoDoc = items.filter(b =>
        b.ano === despesa.ano &&
        b.idEscola === despesa.idEscola &&
        b.idPrograma === despesa.idPrograma &&
        b.idAcao === despesa.idAcao &&
        b.idConta === despesa.idConta &&
        b.idFavorecido === despesa.idFavorecido &&
        b.idNatureza === despesa.idNatureza &&
        b.docNum === despesa.docNum &&
        // exclui o item em edição para não somar duas vezes
        String(b.id) !== String(form.id)
      );
      const somaBens = bensMesmoDoc.reduce((acc, b) => acc + ((parseFloat(b.qtde) || 1) * (parseFloat(b.vlr) || 0) - (parseFloat(b.desconto) || 0)), 0);
      const valorDespesa = parseFloat(despesa.vlr) || 0;
      const valorNovoBem = ((parseFloat(form.qtde) || 1) * (parseFloat(form.vlr) || 0)) - (parseFloat(form.desconto) || 0);
      if (somaBens + valorNovoBem > valorDespesa) {
        showDefaultModal('error', `O valor total dos bens (${formatCurrency(somaBens + valorNovoBem)}) não pode ultrapassar o valor da despesa (${formatCurrency(valorDespesa)}).`);
        return;
      }
    }
    axios.post(`${API_BASE_URL.BENS}/save`, form, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setShowModal(false);
        setAtualizar(a => !a);
        setSelected([]); // Limpa seleção após salvar
      })
      .catch(() => showDefaultModal('error', 'Erro ao salvar bem.'));
  }

  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () => Promise.all(selected.map(s => axios.post(`${API_BASE_URL.BENS}/delete`, {
      id: s.id, ano: s.ano, idPrograma: s.idPrograma, idAcao: s.idAcao, idEscola: s.idEscola, idConta: s.idConta,
      idFavorecido: s.idFavorecido, idNatureza: s.idNatureza, idTipoBem: s.idTipoBem, docNum: s.docNum, docData: s.docData
    }, { headers: { Authorization: `Bearer ${token}` } })))
      .then(() => { setSelected([]); setAtualizar(a => !a); })
      .catch(() => showDefaultModal('error', 'Erro ao excluir bem.'))
    );
  }

  function keyOf(item) { return `${item.id}_${item.ano}_${item.idEscola}_${item.docNum}`; }
  function toggleSelect(item) {
    setSelected(prev => prev.find(s => keyOf(s) === keyOf(item)) ? [] : [item]);
  }

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="BENS" icon="bi-box-seam" closeRoute="/despesas" />
        <PageContent>
          <Toolbar onNew={handleNew} onEdit={handleEdit} disableEdit={selected.length !== 1} onDelete={handleDelete} disableDelete={selected.length === 0}>
          </Toolbar>
          <BensModal
            show={showModal}
            onClose={() => setShowModal(false)}
            onSubmit={handleSave}
            form={form}
            handleChange={handleChange}
            tiposBens={tiposBens}
            naturezas={naturezas}
          />
          <TableContainer isLoading={false}>
                              <DataTable head={<tr>
                  <th style={{ width: '3rem' }}>#</th><th>Descrição</th><th>Natureza</th><th>Tipo Bem</th><th>Qtde</th><th>Valor Unit.</th><th>Desconto</th><th>Total</th>
                </tr>}>
                <tbody>
                  {displayed.map((item, idx) => {
                    // Só exibe tipo de bem se for de natureza 'K'
                    const tipoBem = tiposBens.find(t => t.id === item.idTipoBem && t.idNatureza === 'K');
                    return (
                      <tr key={keyOf(item)} className={selected.find(s => keyOf(s) === keyOf(item)) ? 'table-success' : ''} onClick={() => toggleSelect(item)} style={{ cursor: 'pointer' }}>
                        <td>{setPagination.startIndex + idx + 1}</td>
                        <td>{item.descricao}</td>
                        <td>{(naturezas.find(n => String(n.id) === String(item.idNatureza))?.descricao) || item.idNatureza}</td>
                        <td>{tipoBem ? tipoBem.descricao : ''}</td>
                        <td>{item.qtde}</td>
                        <td>{fmtCurrency(item.vlr)}</td>
                        <td>{fmtCurrency(item.desconto)}</td>
                        <td>{fmtCurrency(Math.max(0, (item.qtde || 1) * (parseFloat(item.vlr) || 0) - (parseFloat(item.desconto) || 0)))}</td>
                      </tr>
                    );
                  })}
                  {/* Linha de total dos bens */}
                  <tr>
                    <td colSpan="7" className="text-end fw-bold">Valor total dos bens:</td>
                    <td className="text-end fw-bold">
                      {fmtCurrency(items.reduce((sum, item) => sum + ((item.qtde || 1) * (parseFloat(item.vlr) || 0) - (parseFloat(item.desconto) || 0)), 0))}
                    </td>
                  </tr>
                </tbody>
              </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      <BensModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSave} form={form} handleChange={handleChange} />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}

export default Bens;
